import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const API = '';

const THEMES = [
  { value: 'business', label: '비즈니스', desc: '깔끔한 비즈니스 스타일', color: '#1a73e8' },
  { value: 'modern', label: '모던', desc: '세련된 모던 디자인', color: '#7c4dff' },
  { value: 'minimal', label: '미니멀', desc: '심플한 미니멀 스타일', color: '#333' },
  { value: 'creative', label: '크리에이티브', desc: '감각적인 컬러풀 디자인', color: '#e040fb' },
  { value: 'dark', label: '다크', desc: '어두운 배경의 프리미엄 스타일', color: '#263238' },
];

const SLIDE_COUNTS = [
  { value: 'auto', label: '자동 (AI 판단)' },
  { value: '5', label: '5장' },
  { value: '10', label: '10장' },
  { value: '15', label: '15장' },
  { value: '20', label: '20장' },
];

function PptAgent() {
  const navigate = useNavigate();
  const [mode, setMode] = useState('text');
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const [theme, setTheme] = useState('business');
  const [slideCount, setSlideCount] = useState('auto');
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [outline, setOutline] = useState(null);
  const [step, setStep] = useState('input'); // input -> outline -> done
  const [includeImages, setIncludeImages] = useState(true);
  const fileRef = useRef(null);

  const handleAnalyze = async () => {
    if (mode === 'text' && !text.trim()) return alert('PPT로 만들 내용을 입력해주세요.');
    if (mode === 'file' && !file) return alert('파일을 선택해주세요.');
    setLoading(true);
    setError('');
    setOutline(null);
    try {
      let res;
      if (mode === 'text') {
        res = await fetch(`${API}/ppt/analyze`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text, title, theme, slide_count: slideCount }),
        });
      } else {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('title', title);
        formData.append('theme', theme);
        formData.append('slide_count', slideCount);
        res = await fetch(`${API}/ppt/analyze`, {
          method: 'POST',
          body: formData,
        });
      }
      const data = await res.json();
      if (data.error) {
        setError(data.error);
      } else {
        setOutline(data.outline);
        setStep('outline');
      }
    } catch (e) {
      setError('분석에 실패했습니다. 서버 연결을 확인해주세요.');
    }
    setLoading(false);
  };

  const handleGenerate = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/ppt/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ outline, title, theme, include_images: includeImages }),
      });
      const data = await res.json();
      if (data.error) {
        setError(data.error);
      } else {
        setResult(data);
        setStep('done');
      }
    } catch (e) {
      setError('PPT 생성에 실패했습니다.');
    }
    setLoading(false);
  };

  const handleDownload = async () => {
    if (!result || !result.file_id) return;
    try {
      const res = await fetch(`${API}/ppt/download/${result.file_id}`);
      const prepData = await res.json(); if (prepData.file_id) { window.open(`${API}/get-download/${prepData.file_id}`, '_self'); return; }
    } catch (e) {
      alert('다운로드에 실패했습니다.');
    }
  };

  const handleReset = () => {
    setText('');
    setFile(null);
    setTitle('');
    setOutline(null);
    setResult(null);
    setError('');
    setStep('input');
  };

  const headerStyle = {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '16px 32px', background: '#fff', borderBottom: '1px solid #e0e0e0',
  };
  const bodyStyle = {
    display: 'flex', gap: 32, padding: 32, maxWidth: 1400, margin: '0 auto',
    fontFamily: "'Pretendard', 'Noto Sans KR', Arial, sans-serif",
  };
  const panelStyle = {
    background: '#fff', borderRadius: 16, boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
    padding: 28, flex: 1,
  };
  const labelStyle = { fontWeight: 700, fontSize: 14, marginBottom: 8, display: 'block', color: '#333' };
  const inputStyle = {
    width: '100%', padding: '12px 14px', borderRadius: 10, border: '1px solid #d0d0d0',
    fontSize: 15, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box',
  };
  const btnPrimary = {
    padding: '14px 0', width: '100%', border: 'none', borderRadius: 12,
    background: 'linear-gradient(135deg, #ff6b35 0%, #e91e63 100%)',
    color: '#fff', fontWeight: 700, fontSize: 17, cursor: 'pointer',
    boxShadow: '0 4px 15px rgba(233,30,99,0.3)',
  };
  const chipStyle = (active) => ({
    display: 'inline-block', padding: '8px 16px', borderRadius: 20,
    border: active ? '2px solid #e91e63' : '1px solid #d0d0d0',
    background: active ? '#fce4ec' : '#fafafa',
    color: active ? '#e91e63' : '#555', fontWeight: active ? 700 : 400,
    fontSize: 13, cursor: 'pointer', margin: '0 6px 8px 0', transition: 'all 0.15s',
  });
  const tabStyle = (active) => ({
    flex: 1, padding: '12px 0', textAlign: 'center', cursor: 'pointer',
    fontWeight: active ? 700 : 400, fontSize: 14,
    color: active ? '#e91e63' : '#888',
    borderBottom: active ? '3px solid #e91e63' : '3px solid transparent',
    transition: 'all 0.15s',
  });

  return (
    <div style={{ background: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <div style={headerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <button onClick={() => navigate('/')} style={{
            background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: '#e91e63',
          }}>&#8592;</button>
          <div>
            <div style={{ fontWeight: 800, fontSize: 20, color: '#333' }}>
              <span style={{ color: '#e91e63' }}>&#9654;</span> PPT &#49373;&#49457; &#50640;&#51060;&#51204;&#53944;
            </div>
            <div style={{ fontSize: 13, color: '#888', marginTop: 2 }}>
              &#53581;&#49828;&#53944; / &#54028;&#51068; &#50629;&#47196;&#46300; &#8594; AI PPT &#51088;&#46041; &#49373;&#49457;
            </div>
          </div>
        </div>
        {step !== 'input' && (
          <button onClick={handleReset} style={{
            padding: '8px 20px', borderRadius: 8, border: '1px solid #d0d0d0',
            background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600,
          }}>&#49352;&#47196; &#49884;&#51089;</button>
        )}
      </div>

      <div style={bodyStyle}>
        {/* Left Panel */}
        <div style={{ ...panelStyle, maxWidth: 440, flex: '0 0 440px' }}>
          {/* Step Indicator */}
          <div style={{
            display: 'flex', gap: 8, marginBottom: 24, alignItems: 'center', justifyContent: 'center',
          }}>
            {['input', 'outline', 'done'].map((s, i) => (
              <React.Fragment key={s}>
                <div style={{
                  width: 32, height: 32, borderRadius: '50%',
                  background: step === s || ['input','outline','done'].indexOf(step) >= i
                    ? '#e91e63' : '#e0e0e0',
                  color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: 14,
                }}>{i + 1}</div>
                {i < 2 && <div style={{
                  flex: 1, height: 2, maxWidth: 40,
                  background: ['input','outline','done'].indexOf(step) > i ? '#e91e63' : '#e0e0e0',
                }} />}
              </React.Fragment>
            ))}
          </div>
          <div style={{ textAlign: 'center', fontSize: 12, color: '#888', marginBottom: 20 }}>
            {step === 'input' ? '1. 내용 입력' : step === 'outline' ? '2. 구성 확인' : '3. PPT 완성'}
          </div>

          {step === 'input' && (
            <>
              <label style={labelStyle}>PPT 제목</label>
              <input
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="프레젠테이션 제목을 입력하세요 (선택)"
                style={{ ...inputStyle, marginBottom: 16 }}
              />

              {/* Mode Tabs */}
              <div style={{ display: 'flex', borderBottom: '1px solid #e0e0e0', marginBottom: 16 }}>
                <div onClick={() => setMode('text')} style={tabStyle(mode === 'text')}>텍스트 입력</div>
                <div onClick={() => setMode('file')} style={tabStyle(mode === 'file')}>파일 업로드</div>
              </div>

              {mode === 'text' ? (
                <>
                  <label style={labelStyle}>내용 입력</label>
                  <textarea
                    value={text}
                    onChange={e => setText(e.target.value)}
                    placeholder={"PPT로 만들 내용을 입력하세요.\n\n예시:\n- 회의 내용, 보고서 초안\n- 주제 키워드만 입력해도 AI가 내용을 구성합니다\n- 마크다운 형식도 지원됩니다"}
                    rows={10}
                    style={{ ...inputStyle, marginBottom: 16 }}
                  />
                </>
              ) : (
                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>파일 선택</label>
                  <div
                    onClick={() => fileRef.current && fileRef.current.click()}
                    style={{
                      border: '2px dashed #d0d0d0', borderRadius: 12, padding: '40px 20px',
                      textAlign: 'center', cursor: 'pointer', background: '#fafafa',
                      transition: 'all 0.15s',
                    }}
                    onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = '#e91e63'; }}
                    onDragLeave={e => { e.currentTarget.style.borderColor = '#d0d0d0'; }}
                    onDrop={e => { e.preventDefault(); e.currentTarget.style.borderColor = '#d0d0d0'; if (e.dataTransfer.files[0]) setFile(e.dataTransfer.files[0]); }}
                  >
                    <input ref={fileRef} type="file" hidden
                      accept=".txt,.pdf,.docx,.doc,.hwp,.md,.csv,.xlsx,.xls,.pptx,.json"
                      onChange={e => { if (e.target.files[0]) setFile(e.target.files[0]); }}
                    />
                    {file ? (
                      <div>
                        <div style={{ fontSize: 32, marginBottom: 8 }}>&#128196;</div>
                        <div style={{ fontWeight: 600, color: '#333' }}>{file.name}</div>
                        <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                          {(file.size / 1024).toFixed(1)} KB
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div style={{ fontSize: 32, marginBottom: 8 }}>&#128228;</div>
                        <div style={{ color: '#888', fontSize: 14 }}>클릭 또는 드래그하여 파일을 업로드하세요</div>
                        <div style={{ color: '#aaa', fontSize: 12, marginTop: 4 }}>
                          TXT, PDF, DOCX, HWP, MD, CSV, XLSX, PPTX, JSON
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <label style={labelStyle}>테마</label>
              <div style={{ marginBottom: 16, display: 'flex', flexWrap: 'wrap' }}>
                {THEMES.map(t => (
                  <span key={t.value} onClick={() => setTheme(t.value)}
                    style={{
                      ...chipStyle(theme === t.value),
                      borderColor: theme === t.value ? t.color : '#d0d0d0',
                      background: theme === t.value ? t.color + '18' : '#fafafa',
                      color: theme === t.value ? t.color : '#555',
                    }}
                    title={t.desc}>{t.label}</span>
                ))}
              </div>

              <label style={labelStyle}>슬라이드 수</label>
              <div style={{ marginBottom: 24, display: 'flex', flexWrap: 'wrap' }}>
                {SLIDE_COUNTS.map(s => (
                  <span key={s.value} onClick={() => setSlideCount(s.value)}
                    style={chipStyle(slideCount === s.value)}>{s.label}</span>
                ))}
              </div>

              <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 24 }}>
                <div onClick={() => setIncludeImages(!includeImages)} style={{
                  width: 44, height: 24, borderRadius: 12,
                  background: includeImages ? '#e91e63' : '#ccc',
                  position: 'relative', transition: 'background 0.2s', cursor: 'pointer',
                }}>
                  <div style={{
                    width: 20, height: 20, borderRadius: '50%', background: '#fff',
                    position: 'absolute', top: 2,
                    left: includeImages ? 22 : 2,
                    transition: 'left 0.2s',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                  }} />
                </div>
                <span>AI 이미지 포함 (표지 + 주요 슬라이드)</span>
              </label>

              <button onClick={handleAnalyze} disabled={loading} style={{
                ...btnPrimary, opacity: loading ? 0.7 : 1,
              }}>
                {loading ? 'AI가 분석 중...' : 'AI 구성 분석'}
              </button>
            </>
          )}

          {step === 'outline' && (
            <>
              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 16, color: '#333' }}>
                슬라이드 구성을 확인하세요
              </div>
              <div style={{ fontSize: 13, color: '#888', marginBottom: 16 }}>
                AI가 분석한 슬라이드 구성입니다. 수정 후 PPT를 생성하세요.
              </div>
              <textarea
                value={typeof outline === 'string' ? outline : JSON.stringify(outline, null, 2)}
                onChange={e => {
                  try { setOutline(JSON.parse(e.target.value)); } catch { setOutline(e.target.value); }
                }}
                rows={16}
                style={{ ...inputStyle, marginBottom: 16, fontSize: 13, fontFamily: 'monospace' }}
              />
              <button onClick={handleGenerate} disabled={loading} style={{
                ...btnPrimary, opacity: loading ? 0.7 : 1,
              }}>
                {loading ? 'PPT 생성 중...' : 'PPT 생성하기'}
              </button>
            </>
          )}

          {step === 'done' && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: 56, marginBottom: 16 }}>&#9989;</div>
              <div style={{ fontWeight: 700, fontSize: 18, color: '#333', marginBottom: 8 }}>
                PPT가 생성되었습니다!
              </div>
              <div style={{ fontSize: 14, color: '#888', marginBottom: 24 }}>
                {result && result.slide_count}장 슬라이드{result && result.image_count > 0 ? ` · ${result.image_count}개 AI 이미지 포함` : ''}
              </div>
              <button onClick={handleDownload} style={{
                ...btnPrimary, marginBottom: 12,
              }}>
                PPTX 다운로드
              </button>
              <button onClick={handleReset} style={{
                ...btnPrimary, background: '#fff', color: '#e91e63',
                border: '2px solid #e91e63', boxShadow: 'none',
              }}>
                새로운 PPT 만들기
              </button>
            </div>
          )}
        </div>

        {/* Right Panel - Preview */}
        <div style={{ ...panelStyle, flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 16, color: '#333' }}>
            {step === 'input' ? '미리보기' : step === 'outline' ? '슬라이드 구성' : 'PPT 미리보기'}
          </div>

          {error && (
            <div style={{
              padding: 16, borderRadius: 12, background: '#ffebee', color: '#c62828',
              fontSize: 14, marginBottom: 16,
            }}>{error}</div>
          )}

          {loading && (
            <div style={{ textAlign: 'center', padding: '60px 0', color: '#888' }}>
              <div style={{
                width: 48, height: 48, border: '4px solid #e0e0e0', borderTop: '4px solid #e91e63',
                borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px',
              }} />
              <div style={{ fontSize: 15 }}>
                {step === 'input' ? 'AI가 내용을 분석하고 있습니다...' :
                  includeImages ? 'AI 이미지를 생성하고 PPT를 만들고 있습니다...' : 'PPT를 생성하고 있습니다...'}
              </div>
              <div style={{ fontSize: 13, color: '#aaa', marginTop: 4 }}>
                {step === 'outline' && includeImages ? '이미지 생성 포함 약 30~90초 소요됩니다' : '약 15~40초 소요됩니다'}
              </div>
              <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
            </div>
          )}

          {!loading && step === 'input' && (
            <div style={{ textAlign: 'center', padding: '60px 0', color: '#bbb' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>&#128202;</div>
              <div style={{ fontSize: 15 }}>내용을 입력하면 AI가 PPT 구성을 분석합니다</div>
            </div>
          )}

          {!loading && outline && (step === 'outline' || step === 'done') && (
            <div>
              {(typeof outline === 'object' && outline.slides ? outline.slides : []).map((slide, i) => (
                <div key={i} style={{
                  marginBottom: 16, borderRadius: 12, overflow: 'hidden',
                  border: '1px solid #e0e0e0', background: '#fff',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                }}>
                  <div style={{
                    padding: '12px 20px',
                    background: theme === 'dark' ? '#263238' : theme === 'creative' ? '#e91e63' : theme === 'modern' ? '#7c4dff' : '#1a73e8',
                    color: '#fff', fontWeight: 700, fontSize: 14,
                    display: 'flex', justifyContent: 'space-between',
                  }}>
                    <span>Slide {i + 1}</span>
                    <span style={{ fontWeight: 400, opacity: 0.8, fontSize: 12 }}>{slide.layout || ''}</span>
                  </div>
                  <div style={{ padding: '16px 20px' }}>
                    <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8, color: '#333' }}>
                      {slide.title || ''}
                    </div>
                    {slide.bullets && slide.bullets.map((b, j) => (
                      <div key={j} style={{ fontSize: 13, color: '#555', padding: '3px 0 3px 16px', position: 'relative' }}>
                        <span style={{ position: 'absolute', left: 0 }}>&#8226;</span>
                        {b}
                      </div>
                    ))}
                    {slide.notes && (
                      <div style={{ fontSize: 11, color: '#aaa', marginTop: 8, fontStyle: 'italic' }}>
                        Notes: {slide.notes}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PptAgent;
